import {
	Mesh,
	PlaneGeometry,
	ImageBitmapLoader,
	Texture,
	DataTexture,
	SRGBColorSpace,
	LinearFilter,
} from 'three/webgpu'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import DomTracked from './DomTracked'
import RoundedImageMaterial from '../materials/RoundedImageMaterial'

const geometry = new PlaneGeometry(1, 1)
const placeholder = new DataTexture(new Uint8Array(4), 1, 1)
const ease = gsap.parseEase('power2.out')

export default class DomImage extends DomTracked {
	static defaults = { x: 160, y: 160, z: 0.5, zoom: 1.3, start: 'top bottom', end: 'top 10%' }

	constructor(options = {}) {
		super(options)

		this.progress = 0
		this.radius =
			parseFloat(getComputedStyle(this.element.closest('[class*="rounded"]') ?? this.element).borderRadius) || 0
		this.material = new RoundedImageMaterial({ map: DomImage.prepare(placeholder) })
		this.mesh = new Mesh(geometry, this.material)
		this.add(this.mesh)

		this.trigger = ScrollTrigger.create({
			trigger: this.element,
			start: this.options.start,
			end: this.options.end,
			scrub: true,
			onUpdate: ({ progress }) => this.#update(ease(progress)),
		})

		this.#load()
	}

	static prepare(texture) {
		texture.colorSpace = SRGBColorSpace
		texture.minFilter = LinearFilter
		texture.generateMipmaps = true
		texture.flipY = false
		texture.needsUpdate = true

		return texture
	}

	async #load() {
		const bitmap = await new ImageBitmapLoader()
			.setOptions({ imageOrientation: 'flipY' })
			.loadAsync(this.element.currentSrc || this.element.src)

		this.texture = DomImage.prepare(new Texture(bitmap))
		this.material.map = this.texture
		this.#fit()
	}

	#fit() {
		const rect = this.element.getBoundingClientRect()
		const { width, height } = this.texture.image
		const scale = Math.max(rect.width / width, rect.height / height)

		this.cover = { x: rect.width / scale / width, y: rect.height / scale / height }
		this.#update(this.progress)
	}

	#update(progress) {
		this.progress = progress

		if (!this.texture) return

		const zoom = 1 + (this.options.zoom - 1) * (1 - progress)

		this.texture.repeat.set(this.cover.x / zoom, this.cover.y / zoom)
		this.texture.offset.set((1 - this.texture.repeat.x) / 2, (1 - this.texture.repeat.y) / 2)
	}

	layout(rect) {
		const remaining = 1 - this.progress
		const z = this.options.z * remaining
		const distance = this.camera.position.z - this.depth

		const side = ((rect.left + rect.width / 2) / window.innerWidth) * 2 - 1

		this.scale.set(rect.width * this.worldPerPixel, rect.height * this.worldPerPixel, 1)
		this.position.x += this.options.x * side * remaining * this.worldPerPixel
		this.position.y -= this.options.y * remaining * this.worldPerPixel
		this.position.z += z
		this.material.size.value.set(rect.width, rect.height)
		this.material.radius.value = (this.radius * (distance - z)) / distance
	}

	onResize() {
		if (this.texture) this.#fit()
	}

	dispose() {
		this.trigger.kill()
		this.material.dispose()
		this.texture?.dispose()
		super.dispose()
	}
}
